import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AppConfig } from '@iupics-config/app.config.service';
import { Observable } from 'rxjs';
import {
  AuditChange,
  AuditDocumentAccess,
  AuditExecution,
  AuditExecutionDetail,
  AuditOption,
  AuditOptionKind,
  AuditPage,
  AuditStopResult,
  AuditSummary,
} from './audit.models';

/** HTTP access to the audit dashboard backend (AuditWS, config path "audit") */
@Injectable({ providedIn: 'root' })
export class AuditService {
  readonly #http = inject(HttpClient);
  readonly #config = inject(AppConfig);

  get #url(): string {
    return this.#config.getBackendResource('audit');
  }

  /** executionsOnly: live refresh, record change counts come back as -1 */
  summary(from: string, to: string, executionsOnly = false): Observable<AuditSummary> {
    const fromObject: Record<string, string> = { from, to };
    if (executionsOnly) fromObject['part'] = 'executions';
    return this.#http.get<AuditSummary>(`${this.#url}/summary`, { params: new HttpParams({ fromObject }) });
  }

  executions(params: Record<string, string>): Observable<AuditPage<AuditExecution>> {
    return this.#http.get<AuditPage<AuditExecution>>(`${this.#url}/executions`, { params: new HttpParams({ fromObject: params }) });
  }

  execution(id: number): Observable<AuditExecutionDetail> {
    return this.#http.get<AuditExecutionDetail>(`${this.#url}/executions/${encodeURIComponent(String(id))}`);
  }

  /** server side stop of a running execution (the node running it cancels it) */
  stopExecution(id: number): Observable<{ result: AuditStopResult }> {
    return this.#http.post<{ result: AuditStopResult }>(`${this.#url}/executions/${encodeURIComponent(String(id))}/stop`, null);
  }

  changes(params: Record<string, string>): Observable<AuditPage<AuditChange>> {
    return this.#http.get<AuditPage<AuditChange>>(`${this.#url}/changes`, { params: new HttpParams({ fromObject: params }) });
  }

  change(id: string): Observable<AuditChange> {
    return this.#http.get<AuditChange>(`${this.#url}/changes/${encodeURIComponent(id)}`);
  }

  documents(params: Record<string, string>): Observable<AuditPage<AuditDocumentAccess>> {
    return this.#http.get<AuditPage<AuditDocumentAccess>>(`${this.#url}/documents`, { params: new HttpParams({ fromObject: params }) });
  }

  /** Filter choices: by part of the name, or by ids (names of the selected ones) */
  options(kind: AuditOptionKind, q: string | null, ids: number[] = []): Observable<AuditOption[]> {
    const fromObject: Record<string, string> = {};
    if (ids.length) fromObject['ids'] = ids.join(',');
    else if (q) fromObject['q'] = q;
    return this.#http.get<AuditOption[]>(`${this.#url}/options/${kind}`, { params: new HttpParams({ fromObject }) });
  }

  /** Export of the filtered list (csv or json, at most 5000 rows), saved by the browser */
  export(kind: 'executions' | 'changes' | 'documents', format: 'csv' | 'json', params: Record<string, string>): Observable<Blob> {
    const fromObject = { ...params, format };
    delete fromObject['offset'];
    delete fromObject['limit'];
    return this.#http.get(`${this.#url}/export/${kind}`, { params: new HttpParams({ fromObject }), responseType: 'blob' });
  }
}
